import { useMutation, useQuery, useQueryClient } from "react-query";
import { AxiosInstance } from "../axios.config";
import { api } from "../constants";
import { IGetUserParams, IRoles, RoleTitle, UserListResponse } from "./types";
import { IAddUserData } from "@/pages/UserManagement/AddUserForm";

const getUserList = async (
  params: IGetUserParams
): Promise<UserListResponse> => {
  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(([_, value]) => value != null && value !== "")
  );

  const response = await AxiosInstance.get<UserListResponse>(
    api.userManagement.list,
    {
      params: filteredParams,
    }
  );

  return response.data;
};

const useGetUserList = (params: IGetUserParams) => {
  return useQuery<UserListResponse>({
    queryKey: ["user-list"],
    queryFn: () => getUserList(params),
  });
};
const getUserById = async (id: number) => {
  return await AxiosInstance.get(`${api.userManagement.list}/?id=${id}`);
};

const useGetUserById = (id: number) => {
  return useQuery({
    queryKey: ["user-by-id", id],
    queryFn: () => getUserById(id),
    enabled: !!id,
  });
};

const getUserRoleTitles = async (): Promise<RoleTitle[]> => {
  const response = await AxiosInstance.get(api.userManagement.role_titles);
  return response.data;
};

const useGetUserRoleTitles = () => {
  return useQuery<RoleTitle[], Error>({
    queryKey: ["user-role-titles"],
    queryFn: getUserRoleTitles,
  });
};

const getUserRoles = async (): Promise<IRoles[]> => {
  const response = await AxiosInstance.get<IRoles[]>(api.userManagement.roles);
  return response.data;
};

const useGetUserRoles = () => {
  return useQuery<IRoles[]>({
    queryKey: ["user-roles"],
    queryFn: getUserRoles,
  });
};

const createUser = (data: IAddUserData) => {
  return AxiosInstance.post(api.userManagement.create_user, data);
};

const useCreateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: "create_user",
    mutationFn: createUser,
    onSuccess: () => {
      queryClient.invalidateQueries("user-list");
    },
  });
};

const updateUser = (data: IAddUserData) => {
  const { id, ...payload } = data;
  if (!id) {
    return Promise.reject("User ID is required");
  }
  return AxiosInstance.patch(
    api.userManagement.update_user.replace(":id", id.toString()),
    payload
  );
};

const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation((data: IAddUserData) => updateUser(data), {
    mutationKey: "update_user",
    onSuccess: () => {
      queryClient.invalidateQueries("user-list");
    },
  });
};

const deleteUser = (id: number) => {
  return AxiosInstance.delete(
    `${api.userManagement.delete_user.replace(":id", id.toString())}`
  );
};

const useDeleteUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: "delete_user",
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries("user-list");
    },
  });
};

export {
  useGetUserList,
  useGetUserRoleTitles,
  useGetUserRoles,
  useCreateUser,
  useDeleteUser,
  useGetUserById,
  useUpdateUser,
};
